"use client";
import { useRouter } from "next/navigation";
import { ChevronRight } from "lucide-react";

interface IntegratorCardButtonProps {
  // Sin sesión invita a iniciar sesión; con sesión (cliente aún no vinculado)
  // invita a completar la solicitud de integrador.
  isGuest: boolean;
}

// Botón de la card de producto para ver precios de integrador. Es un <button>
// (no un enlace) porque la card completa ya es un <Link>.
export function IntegratorCardButton({ isGuest }: IntegratorCardButtonProps) {
  const router = useRouter();
  const href = isGuest ? "/iniciar-sesion" : "/usuario/datos-comerciales";
  const title = isGuest ? "Inicia sesión" : "Completa tu solicitud";

  return (
    <button
      type="button"
      data-no-navigate="true"
      aria-label={`${title}. Accede a precios exclusivos para integradores`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        router.push(href);
      }}
      className="no-navigate @container/cta group/cta relative isolate grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] @min-[150px]/cta:grid-cols-[minmax(0,1fr)_2rem] items-center gap-x-2 @min-[150px]/cta:gap-x-2.5 overflow-hidden rounded-lg border border-primary/20 bg-card px-2.75 py-2.25 text-left transition-[border-color,transform,box-shadow] duration-200 before:absolute before:inset-0 before:-z-10 before:w-0 before:rounded-[inherit] before:bg-primary before:transition-[width] before:duration-300 before:ease-[cubic-bezier(.22,.61,.36,1)] after:pointer-events-none after:absolute after:-left-[35%] after:-top-[30%] after:h-[160%] after:w-[22%] after:-skew-x-18 after:bg-linear-to-r after:from-white/0 after:via-white/20 after:to-white/0 after:opacity-0 hover:scale-[1.012] hover:border-primary hover:shadow-[0_3px_10px_color-mix(in_srgb,var(--primary)_16%,transparent),0_0_0_3px_color-mix(in_srgb,var(--primary)_5%,transparent)] hover:before:w-full hover:after:animate-cta-scan hover:after:opacity-100 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-primary/30 motion-reduce:transition-none motion-reduce:before:transition-none motion-reduce:hover:after:animate-none"
    >
      <span className="col-start-1 row-start-1 text-xs font-bold leading-tight text-primary underline underline-offset-2 transition-[color,transform] duration-200 group-hover/cta:translate-x-0.5 group-hover/cta:text-primary-foreground motion-reduce:transition-none">
        {title}
      </span>
      <span className="col-span-2 col-start-1 row-start-2 mt-1 @min-[150px]/cta:col-span-1 text-[10.5px] font-medium leading-tight text-muted-foreground transition-[color,transform] duration-200 group-hover/cta:translate-x-0.5 group-hover/cta:text-primary-foreground motion-reduce:transition-none">
        Accede a <strong>precios exclusivos</strong> para integradores
      </span>
      <span
        aria-hidden="true"
        className="relative col-start-2 row-start-1 @min-[150px]/cta:row-span-2 inline-flex size-5.5 items-center justify-center justify-self-end overflow-hidden rounded-full bg-primary/10 text-primary transition-all duration-200 group-hover/cta:w-8 group-hover/cta:translate-x-px group-hover/cta:bg-primary-foreground/20 group-hover/cta:text-primary-foreground group-hover/cta:shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--primary-foreground)_22%,transparent)] motion-reduce:transition-none"
      >
        <ChevronRight className="absolute size-3.5 transition-[opacity,transform] duration-200 group-hover/cta:-translate-x-0.75 group-hover/cta:scale-[0.84] group-hover/cta:opacity-0 motion-reduce:transition-none" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute size-4.5 translate-x-0.75 scale-[0.88] opacity-0 transition-[opacity,transform] duration-200 group-hover/cta:translate-x-0 group-hover/cta:scale-100 group-hover/cta:opacity-100 motion-reduce:transition-none"
        >
          <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.7" />
          <path
            d="M4.5 17.2C5.4 14.9 7.2 13.8 9 13.8C10.8 13.8 12.6 14.9 13.5 17.2"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
          <path
            d="M14 12H20"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
          <path
            d="M17.2 9.4L20 12L17.2 14.6"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}
